import React, { useMemo } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import { IconChevronRight, IconCheck, IconClock, IconSend, IconHourglass } from '@tabler/icons-react';
import { usePayrollRunsAssignedToMe } from '../../hooks/usePayroll';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { TableLoader } from '../../components/common/Loader';
import { getAssigneeFlags } from './utils';

const STATUS_STYLES = {
    draft: 'bg-warning/10 text-warning',
    in_review: 'bg-sky/10 text-sky',
    submitted: 'bg-sky/10 text-sky',
    approved: 'bg-success/10 text-success',
    published: 'bg-purple-500/10 text-purple-500',
    rejected: 'bg-danger/10 text-danger',
};

// Personal queue: runs where the current user is a reviewer or the approver, regardless of permission or org.
export default function PayrollApprovals() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { data: myRuns, isLoading } = usePayrollRunsAssignedToMe();

    const { awaitingReview, awaitingApproval, upNext, history, isReviewerAnywhere, isApproverAnywhere } = useMemo(() => {
        const runs = myRuns || [];
        const employeeId = user?.employeeId;
        const awaitingReview = [];
        const awaitingApproval = [];
        const upNext = [];
        const history = [];
        let isReviewerAnywhere = false;
        let isApproverAnywhere = false;

        runs.forEach(run => {
            const { canReview, canApprove } = getAssigneeFlags(run);
            if (canReview) {
                awaitingReview.push(run);
            } else if (canApprove) {
                awaitingApproval.push(run);
            } else if (run.status === 'draft' || run.status === 'in_review' || run.status === 'rejected') {
                // Chain hasn't reached this user's step yet — not done, just not their turn.
                upNext.push(run);
            } else {
                history.push(run);
            }

            if (employeeId) {
                if ((run.review_steps || []).some(s => String(s.reviewer) === String(employeeId))) isReviewerAnywhere = true;
                if (run.approve_assignee && String(run.approve_assignee) === String(employeeId)) isApproverAnywhere = true;
            }
        });

        return { awaitingReview, awaitingApproval, upNext, history, isReviewerAnywhere, isApproverAnywhere };
    }, [myRuns, user]);

    const pendingCount = awaitingReview.length + awaitingApproval.length;
    const hasNothing = !isLoading && !isReviewerAnywhere && !isApproverAnywhere;

    return (
        <>
            <Breadcrumb title="Payroll Approvals" path="Approvals / Payroll" />

            {/* Pending Badge */}
            {pendingCount > 0 && (
                <div className="bg-sky/10 border border-sky/30 rounded-lg p-15 mb-20 flex items-center gap-10">
                    <IconClock size={20} className="text-sky shrink-0" />
                    <p className="text-sm font-medium text-sky">
                        {pendingCount} payroll run{pendingCount !== 1 ? 's' : ''} awaiting your action.
                    </p>
                </div>
            )}

            {hasNothing && (
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                    <TableLoader text="You are not assigned as a reviewer or approver on any payroll run." />
                </div>
            )}

            {/* Awaiting My Review */}
            {isReviewerAnywhere && (
            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 mb-20">
                <h2 className="text-[15px]/[20px] font-semibold mb-15 flex items-center gap-8">
                    <IconSend size={18} className="text-warning" />
                    Awaiting My Review
                </h2>

                {isLoading ? (
                    <TableLoader />
                ) : awaitingReview.length === 0 ? (
                    <TableLoader text="
                        No payroll runs are currently awaiting your review.
                    " />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                            <thead>
                                <tr className="bg-bg-light-100">
                                    <th align="left" className="p-10 font-medium">Run Label</th>
                                    <th align="left" className="p-10 font-medium">Triggered By</th>
                                    <th align="center" className="p-10 font-medium">Employees</th>
                                    <th align="center" className="p-10 font-medium">Status</th>
                                    <th align="center" className="p-10 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {awaitingReview.map(run => (
                                    <tr
                                        key={run.id}
                                        className="border-t border-border hover:bg-bg-light cursor-pointer"
                                        onClick={() => navigate(`/payroll/runs/${run.id}`)}
                                    >
                                        <td className="p-10 font-medium">{run.label}</td>
                                        <td className="p-10 text-text-secondary">{run.triggered_by_name || '—'}</td>
                                        <td className="p-10 text-center">{run.record_count}</td>
                                        <td className="p-10 text-center">
                                            <span className={`px-8 py-3 rounded-full text-xs capitalize ${STATUS_STYLES[run.status] || ''}`}>
                                                {run.status}
                                            </span>
                                        </td>
                                        <td className="p-10 text-center">
                                            <button
                                                onClick={e => { e.stopPropagation(); navigate(`/payroll/runs/${run.id}`); }}
                                                className="flex items-center gap-3 text-primary text-sm mx-auto font-medium"
                                            >
                                                Review <IconChevronRight size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            )}

            {/* Awaiting My Approval */}
            {isApproverAnywhere && (
            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 mb-20">
                <h2 className="text-[15px]/[20px] font-semibold mb-15 flex items-center gap-8">
                    <IconClock size={18} className="text-sky" />
                    Awaiting My Approval
                </h2>

                {isLoading ? (
                    <TableLoader />
                ) : awaitingApproval.length === 0 ? (
                    <TableLoader text="
                        No payroll runs are currently awaiting your approval.
                    " />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                            <thead>
                                <tr className="bg-bg-light-100">
                                    <th align="left" className="p-10 font-medium">Run Label</th>
                                    <th align="left" className="p-10 font-medium">Submitted By</th>
                                    <th align="center" className="p-10 font-medium">Employees</th>
                                    <th align="left" className="p-10 font-medium">Date Submitted</th>
                                    <th align="center" className="p-10 font-medium">Status</th>
                                    <th align="center" className="p-10 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {awaitingApproval.map(run => (
                                    <tr
                                        key={run.id}
                                        className="border-t border-border hover:bg-bg-light cursor-pointer"
                                        onClick={() => navigate(`/payroll/runs/${run.id}`)}
                                    >
                                        <td className="p-10 font-medium">{run.label}</td>
                                        <td className="p-10 text-text-secondary">{run.reviewed_by_name || run.triggered_by_name || '—'}</td>
                                        <td className="p-10 text-center">{run.record_count}</td>
                                        <td className="p-10 text-text-secondary">
                                            {run.submitted_at ? new Date(run.submitted_at).toLocaleDateString('en-GB', {
                                                day: '2-digit', month: 'short', year: 'numeric',
                                            }) : '—'}
                                        </td>
                                        <td className="p-10 text-center">
                                            <span className={`px-8 py-3 rounded-full text-xs capitalize ${STATUS_STYLES[run.status] || ''}`}>
                                                {run.status}
                                            </span>
                                        </td>
                                        <td className="p-10 text-center">
                                            <button
                                                onClick={e => { e.stopPropagation(); navigate(`/payroll/runs/${run.id}`); }}
                                                className="flex items-center gap-3 text-primary text-sm mx-auto font-medium"
                                            >
                                                Review <IconChevronRight size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            )}

            {/* Waiting on Others */}
            {upNext.length > 0 && (
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 mb-20">
                    <h2 className="text-[15px]/[20px] font-semibold mb-15 flex items-center gap-8">
                        <IconHourglass size={18} className="text-text-secondary" />
                        Waiting on Others
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                            <thead>
                                <tr className="bg-bg-light-100">
                                    <th align="left" className="p-10 font-medium">Run Label</th>
                                    <th align="left" className="p-10 font-medium">Triggered By</th>
                                    <th align="center" className="p-10 font-medium">Employees</th>
                                    <th align="left" className="p-10 font-medium">Current Reviewer</th>
                                    <th align="center" className="p-10 font-medium">Status</th>
                                    <th align="center" className="p-10 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {upNext.map(run => (
                                    <tr
                                        key={run.id}
                                        className="border-t border-border hover:bg-bg-light cursor-pointer"
                                        onClick={() => navigate(`/payroll/runs/${run.id}`)}
                                    >
                                        <td className="p-10 font-medium">{run.label}</td>
                                        <td className="p-10 text-text-secondary">{run.triggered_by_name || '—'}</td>
                                        <td className="p-10 text-center">{run.record_count}</td>
                                        <td className="p-10 text-text-secondary">{run.current_reviewer_name || '—'}</td>
                                        <td className="p-10 text-center">
                                            <span className={`px-8 py-3 rounded-full text-xs capitalize ${STATUS_STYLES[run.status] || ''}`}>
                                                {run.status}
                                            </span>
                                        </td>
                                        <td className="p-10 text-center">
                                            <button
                                                onClick={e => { e.stopPropagation(); navigate(`/payroll/runs/${run.id}`); }}
                                                className="flex items-center gap-3 text-primary text-sm mx-auto"
                                            >
                                                View <IconChevronRight size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* History */}
            {history.length > 0 && (
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                    <h2 className="text-[15px]/[20px] font-semibold mb-15 flex items-center gap-8">
                        <IconCheck size={18} className="text-success" />
                        Previously Actioned
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                            <thead>
                                <tr className="bg-bg-light-100">
                                    <th align="left" className="p-10 font-medium">Run Label</th>
                                    <th align="left" className="p-10 font-medium">Triggered By</th>
                                    <th align="center" className="p-10 font-medium">Employees</th>
                                    <th align="center" className="p-10 font-medium">Status</th>
                                    <th align="center" className="p-10 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(run => (
                                    <tr
                                        key={run.id}
                                        className="border-t border-border hover:bg-bg-light cursor-pointer opacity-70"
                                        onClick={() => navigate(`/payroll/runs/${run.id}`)}
                                    >
                                        <td className="p-10 font-medium">{run.label}</td>
                                        <td className="p-10 text-text-secondary">{run.triggered_by_name || '—'}</td>
                                        <td className="p-10 text-center">{run.record_count}</td>
                                        <td className="p-10 text-center">
                                            <span className={`px-8 py-3 rounded-full text-xs capitalize ${STATUS_STYLES[run.status] || ''}`}>
                                                {run.status}
                                            </span>
                                        </td>
                                        <td className="p-10 text-center">
                                            <button
                                                onClick={e => { e.stopPropagation(); navigate(`/payroll/runs/${run.id}`); }}
                                                className="flex items-center gap-3 text-primary text-sm mx-auto"
                                            >
                                                View <IconChevronRight size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
}
